import React, { useEffect } from "react";
import { connect } from "react-redux";
import "bootstrap/dist/css/bootstrap.min.css";
import moment from "moment";
import { fetchActivityLogs } from "../Reducers/actions";

const ActivityLogs = ({ gastenverbruikId, logs, loading, error, fetchActivityLogs }) => {
  useEffect(() => {
    if (gastenverbruikId) {
      fetchActivityLogs(gastenverbruikId);
    }
  }, [gastenverbruikId, fetchActivityLogs]);

  const handleRefresh = () => {
    if (gastenverbruikId) {
      fetchActivityLogs(gastenverbruikId);
    }
  };

  const getActionTypeIcon = (actionType) => {
    const icons = {
      'CREATE_BOOKING': 'fas fa-plus-circle text-success',
      'UPDATE_BOOKING': 'fas fa-edit text-primary',
      'DELETE_BOOKING': 'fas fa-trash-alt text-danger',
      'ACTIVATE_RESERVATION': 'fas fa-power-off text-success',
      'FINISH_BOOKING': 'fas fa-check-circle text-success',
      'ADD_KWH': 'fas fa-bolt text-warning',
      'CHANGE_AMPERE': 'fas fa-plug text-info',
      'MOVE_BOOKING': 'fas fa-map-marker-alt text-primary',
      'PROCESS_PAYMENT': 'fas fa-credit-card text-success',
      'ADD_EXTRA_CREDIT': 'fas fa-coins text-warning',
      'SENSOR_COUPLING': 'fas fa-link text-info',
      'METER_READING': 'fas fa-chart-bar text-primary',
      'STATUS_CHANGE': 'fas fa-sync-alt text-info',
      'EMAIL_SENT': 'fas fa-envelope text-primary',
      'WEBHOOK_RECEIVED': 'fas fa-globe text-info',
      'EARLY_ARRIVAL': 'fas fa-clock text-warning'
    };
    return icons[actionType] || 'fas fa-clipboard text-secondary';
  };

  const getActionTypeBadge = (actionType, success) => {
    const baseClasses = "badge";
    const bgClass = success ? "bg-success" : "bg-danger";
    
    const typeLabels = {
      'CREATE_BOOKING': 'Aangemaakt',
      'UPDATE_BOOKING': 'Gewijzigd',
      'DELETE_BOOKING': 'Verwijderd',
      'ACTIVATE_RESERVATION': 'Geactiveerd',
      'FINISH_BOOKING': 'Afgerond',
      'ADD_KWH': 'kWh Toegevoegd',
      'CHANGE_AMPERE': 'Ampere Gewijzigd',
      'MOVE_BOOKING': 'Verplaatst',
      'PROCESS_PAYMENT': 'Betaling',
      'ADD_EXTRA_CREDIT': 'Extra Tegoed',
      'SENSOR_COUPLING': 'Sensor Gekoppeld',
      'METER_READING': 'Meterstand',
      'STATUS_CHANGE': 'Status Gewijzigd',
      'EMAIL_SENT': 'Email Verzonden',
      'WEBHOOK_RECEIVED': 'Webhook',
      'EARLY_ARRIVAL': 'Vroege Aankomst'
    };

    return (
      <span className={`${baseClasses} ${bgClass}`}>
        {typeLabels[actionType] || actionType}
      </span>
    );
  };

  const getUserType = (performedBy) => {
    const userTypeLabels = {
      'admin': 'Admin',
      'manager': 'Manager',
      'medewerker': 'Medewerker',
      'system': 'Systeem',
      'webhook': 'Webhook',
      'guest': 'Gast'
    };
    return userTypeLabels[performedBy?.user_type] || 'Onbekend';
  };

  if (loading) {
    return (
      <div className="container my-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Laden...</span>
          </div>
          <p>Activity logs laden...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container my-4">
        <div className="alert alert-danger">
          <strong>Fout:</strong> {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container my-4">
      <div className="d-flex justify-content-between align-items-center mb-3">

        <button 
          className="btn btn-sm btn-outline-secondary" 
          onClick={handleRefresh}
          title="Ververs"
        >
          <i className="fas fa-sync-alt"></i>
        </button>
      </div>

      {logs.length === 0 ? (
        <div className="alert alert-info">
          <i className="fas fa-info-circle me-2"></i>
          Geen activiteiten gevonden voor deze boeking.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead className="table-light">
              <tr>
                <th className="d-none d-md-table-cell">Tijd</th>
                <th>Actie</th>
                <th>Beschrijving</th>
                <th className="d-none d-lg-table-cell">Door</th>
                <th className="d-none d-xl-table-cell">Status</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, index) => (
                <tr key={log._id || index}>
                  <td className="d-none d-md-table-cell">
                    <small className="text-muted">
                      {moment(log.created_at).format('DD-MM HH:mm')}
                    </small>
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      <i className={`${getActionTypeIcon(log.action_type)} me-2`} style={{ fontSize: '1.2em' }}></i>
                      <div>
                        {getActionTypeBadge(log.action_type, log.success)}
                        <div className="d-block d-md-none">
                          <small className="text-muted">
                            {moment(log.created_at).format('DD-MM HH:mm')}
                          </small>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>
                      {log.description}
                      {log.error_message && (
                        <div className="text-danger small mt-1">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          {log.error_message}
                        </div>
                      )}
                    </div>
                    {/* Mobile view: show user info */}
                    <div className="d-block d-lg-none mt-1">
                      <small className="text-muted">
                        Door: {log.performed_by?.user_name || 'Onbekend'} ({getUserType(log.performed_by)})
                      </small>
                    </div>
                  </td>
                  <td className="d-none d-lg-table-cell">
                    <div>
                      <strong>{log.performed_by?.user_name || 'Onbekend'}</strong>
                      <div>
                        <small className="text-muted">
                          {getUserType(log.performed_by)}
                        </small>
                      </div>
                    </div>
                  </td>
                  <td className="d-none d-xl-table-cell">
                    {log.success ? (
                      <span className="badge bg-success">
                        <i className="fas fa-check me-1"></i>
                        Succesvol
                      </span>
                    ) : (
                      <span className="badge bg-danger">
                        <i className="fas fa-times me-1"></i>
                        Mislukt
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {logs.length > 0 && (
        <div className="text-muted small mt-2 text-center">
          {logs.length} activiteit{logs.length !== 1 ? 'en' : ''} getoond
        </div>
      )}
    </div>
  );
};

// Map state to props
function mapStateToProps(state) {
  return {
    logs: state.gastenverbruik.activityLogs,
    loading: state.gastenverbruik.activityLogsLoading,
    error: state.gastenverbruik.activityLogsError
  };
}

export default connect(mapStateToProps, { fetchActivityLogs })(ActivityLogs);